import React, { useEffect, useState } from "react";
import { playRight, playWrong, playClap } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Blocks, Undo2, RotateCcw, Play, ArrowRight, Trophy } from "lucide-react";
import { NotationStave } from "@/components/NotationDiagrams";
import { StaffViewer } from "@/components/StaffViewer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WeeklyBoard } from "@/components/WeeklyBoard";
import { reportScore } from "@/lib/gameScores";

const barsUnit = (n) => `${n} bar${n === 1 ? "" : "s"}`;

const TRIP = 1.00001; // a group of 3 triplet quavers = 1 beat (tiny offset tells it apart from a crotchet)
const E = 1e-4;
const NOTES = {
  basic: [[1, "Crotchet", "1 beat"], [2, "Minim", "2 beats"]],
  advanced: [[0.5, "Quaver", "½ beat"], [1, "Crotchet", "1 beat"], [2, "Minim", "2 beats"], [3, "Dotted minim", "3 beats"], [4, "Semibreve", "4 beats"],
    [-0.5, "Quaver rest", "½ beat of silence"], [-1, "Crotchet rest", "1 beat of silence"], [-2, "Minim rest", "2 beats of silence"]],
  lesson5: [[0.25, "Semiquaver", "¼ beat", "semiquaver"], [0.5, "Quaver", "½ beat", "quaver"], [1, "Crotchet", "1 beat", "crotchet"], [1.5, "Dotted crotchet", "1½ beats", "dotted-crotchet"],
    [TRIP, "Triplet quavers", "3 notes in 1 beat", "triplet"], [2, "Minim", "2 beats", "minim"], [3, "Dotted minim", "3 beats", "dotted-minim"],
    [-0.5, "Quaver rest", "½ beat of silence", "rest-quaver"], [-1, "Crotchet rest", "1 beat of silence", "rest-crotchet"]],
};
const TIMES = [4, 3, 2];
const fmt = (d) => {
  const a = Math.abs(d);
  if (Math.abs(a - TRIP) < 1e-7) return "1 (triplet)";
  const r = Math.round(a * 4) / 4, whole = Math.floor(r), frac = { 0.25: "¼", 0.5: "½", 0.75: "¾" }[r - whole] || "";
  return frac ? `${whole || ""}${frac}` : String(whole);
};
const L5_DUR = { 0.25: { duration: "16th" }, 0.5: { duration: "eighth" }, 1: { duration: "quarter" }, 1.5: { duration: "quarter", dot: true }, 2: { duration: "half" }, 3: { duration: "half", dot: true } };
const L5_REST = { 0.5: "quaver", 1: "crotchet" };
const toStaff = (bar) => [...bar.flatMap((d) => (d === TRIP ? [0, 1, 2].map(() => ({ pitch: "B4", duration: "eighth", tuplet: 3 }))
  : d < 0 ? [{ rest: L5_REST[-d] }] : [{ pitch: "B4", ...L5_DUR[d] }])), { barline: "double" }];
const show = (d) => (d < 0 ? `(${fmt(d)})` : fmt(d));
const pickTime = (prev) => { let t; do { t = TIMES[Math.floor(Math.random() * TIMES.length)]; } while (t === prev); return t; };
const BEST_KEY = (lv) => `jt-bar-builder-best-${lv}`;

// Fill a bar with note values until it adds up exactly to the time signature.
export const BarBuilder = ({ level = "basic", testId = "bar-builder" }) => {
  const [top, setTop] = useState(4);
  const [bar, setBar] = useState([]);
  const [msg, setMsg] = useState(null);
  const [built, setBuilt] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY(level)) || 0));
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  useEffect(() => { api.get(`/bar-builder/leaderboard?level=${level}`).then(({ data }) => setBoard(data)).catch(() => {}); }, [level]);
  const submit = (n) => { if (user) api.post("/bar-builder/score", { bars: n, level }).then(({ data }) => setBoard(data)).catch(() => {}); };
  const total = bar.reduce((a, b) => a + Math.abs(b), 0);
  const done = Math.abs(total - top) < E;

  const play = (notes) => {
    let t = 0;
    const beat = 0.6;
    notes.forEach((d) => {
      if (d === TRIP) [0, 1, 2].forEach((k) => setTimeout(() => playClap(), (t + k / 3) * beat * 1000));
      else if (d > 0) setTimeout(() => playClap(), t * beat * 1000);
      t += Math.abs(d);
    });
  };
  const add = (d, name) => {
    if (done) return;
    const len = Math.abs(d);
    if (total + len > top + E) { setMsg({ bad: true, text: `Too many beats! A ${name.toLowerCase()} would make ${fmt(total + len)}, but a ${top}/4 bar only holds ${top}. You have ${fmt(top - total)} beat${top - total <= 1 + E ? "" : "s"} left.` }); playWrong(); return; }
    const next = [...bar, d];
    setBar(next);
    if (Math.abs(total + len - top) < E) {
      const n = built + 1;
      setBuilt(n);
      if (n > best) { setBest(n); localStorage.setItem(BEST_KEY(level), String(n)); }
      submit(n);
      if (user) reportScore("bar-builder", level === "lesson5" ? "Bar Builder: semiquavers, dotted notes & triplets" : level === "advanced" ? "Bar Builder: all note values" : "Bar Builder", "round", n);
      playRight();
      setMsg({ bad: false, text: `Bar complete! ${next.map(show).join(" + ")} = ${top}. It adds up to the time signature.` });
      play(next);
    } else setMsg(null);
  };
  const nextBar = () => { setTop(pickTime(top)); setBar([]); setMsg(null); };
  const reset = () => { setBar([]); setMsg(null); };

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Blocks size={14} /> Bar Builder{level === "lesson5" ? ": semiquavers, dotted notes & triplets" : level === "advanced" ? ": all note values" : ""}</p>
        <p className="flex items-center gap-3 text-xs text-muted-foreground">
          <span data-testid={`${testId}-built`}>Bars built: <b className="text-foreground">{built}</b></span>
          <SoundToggle testId={`${testId}-sound-toggle`} />
          <span className="flex items-center gap-1" data-testid={`${testId}-best`}><Trophy size={12} className="text-primary" /> Best: {best}</span>
        </p>
      </div>
      <p className="mt-2 text-sm font-semibold" data-testid={`${testId}-goal`}>Fill this <span className="text-primary">{top}/4</span> bar so it adds up to exactly {top} crotchet beats.</p>
      <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-background/60 p-2">
        {level === "lesson5"
          ? <div className="min-w-[420px]"><StaffViewer key={bar.length} clef="treble" time={`${top}/4`} notes={toStaff(bar)} width={Math.max(460, 220 + bar.length * 70)} playOnClick={false} testId={`${testId}-stave`} /></div>
          : <NotationStave top={top} bottom={4} bars={[bar]} minW="min-w-[300px]" padBeats={top - total} fixedW={Math.max(420, 200 + top * 46 + bar.filter((d) => Math.abs(d) < 1).length * 23)} testId={`${testId}-stave`} />}
      </div>
      <div className="mt-2 flex items-center gap-2" data-testid={`${testId}-meter`}>
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${(total / top) * 100}%` }} /></div>
        <span className="font-mono text-xs font-bold" data-testid={`${testId}-total`}>{fmt(total)} / {top}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {NOTES[level].map(([d, name, len, id]) => (
          <button key={d} type="button" onClick={() => add(d, name)} disabled={done} data-testid={`${testId}-add-${id || `${d < 0 ? "rest-" : ""}${String(Math.abs(d)).replace(".", "-")}`}`}
            className={`flex flex-col items-start rounded-xl border px-3 py-2 ${d < 0 ? "border-dashed border-primary/50" : "border-border"} text-left transition-colors hover:border-primary hover:bg-primary/10 disabled:opacity-40`}>
            <span className="text-sm font-semibold">{name}</span><span className="text-[11px] text-muted-foreground">{len}</span>
          </button>
        ))}
      </div>
      {msg && <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-semibold ${msg.bad ? "bg-red-500/10 text-red-500" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`} data-testid={`${testId}-msg`}>{msg.text}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => { setBar(bar.slice(0, -1)); setMsg(null); }} disabled={!bar.length || done} data-testid={`${testId}-undo`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"><Undo2 size={13} /> Undo</button>
        <button type="button" onClick={reset} disabled={!bar.length} data-testid={`${testId}-clear`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"><RotateCcw size={13} /> Clear</button>
        {done && <button type="button" onClick={() => play(bar)} data-testid={`${testId}-play`} className="inline-flex items-center gap-1.5 rounded-full border border-primary px-3 py-1.5 text-xs font-semibold text-primary"><Play size={13} /> Hear it</button>}
        {done && <button type="button" onClick={nextBar} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">Next bar <ArrowRight size={13} /></button>}
      </div>
      <WeeklyBoard title="This week's Bar Builder board" board={board} testId={testId} user={user} field="bars" unit={barsUnit} empty="Complete a bar to get on the board." />
    </div>
  );
};
